/**
 * GPU particle system — shared types, config, and the common emitter interface.
 *
 * `IParticleEmitter` is the swap point: both the existing CPU `ParticleEmitter`
 * and the new `GpuParticleEmitter` satisfy it, so callers (and the
 * `GpuParticleModule.createEmitter()` fallback) can treat either uniformly.
 * The CPU class already exposes every member listed here — this interface is
 * purely additive and does NOT change its public API.
 */

import type * as THREE from 'three/webgpu';
import type { ParticleConfig, EmitterShape } from '../types.js';

/**
 * Common surface implemented by both the CPU `ParticleEmitter` and the GPU
 * `GpuParticleEmitter`. Kept deliberately small — the union of what the CPU
 * emitter already exposes and what the GPU emitter can cheaply mirror.
 */
export interface IParticleEmitter {
  /** Resolved configuration (read-only contract; do not mutate after create). */
  readonly config: ParticleConfig;
  /** Hard capacity — max particles alive at once. */
  readonly maxParticles: number;
  /** Whether the emitter is currently emitting + simulating. */
  readonly playing: boolean;
  /** World transform used for world-space simulation / spawn placement. */
  readonly worldMatrix: THREE.Matrix4;

  /** Resume emission + simulation. */
  play(): void;
  /** Pause emission + simulation (state preserved). */
  pause(): void;
  /** Stop and reset (clears live particles, resets timers). */
  stop(): void;
  /** Emit `count` particles immediately (a burst). */
  emit(count: number): void;
  /**
   * Advance the emitter by `dt` seconds. For the GPU emitter this only
   * schedules spawn batches + advances CPU-side accumulators; the heavy
   * simulation runs in the compute kernel dispatched by `GpuParticleSystem`.
   */
  update(dt: number): void;

  /** Best-effort count of currently-alive particles (GPU is an estimate). */
  getActiveCount(): number;
  /**
   * Set the continuous emission rate (particles/sec). This is the contract-honest
   * way to change emission after create — the `config` object is read-only and
   * must not be mutated by callers.
   */
  setEmissionRate(rate: number): void;
  /** Backend that actually runs the simulation. */
  readonly backend: ParticleBackend;
}

export type ParticleBackend = 'gpu' | 'cpu';

// ─── GPU storage layout ──────────────────────────────────────────────────────
//
// SoA in three vec4 storage buffers (vec4 keeps WGSL std430 alignment trivial
// and matches the crowd/rain compute precedent in this codebase):
//
//   posBuffer  : vec4 = (px, py, pz, age)
//   velBuffer  : vec4 = (vx, vy, vz, size)        ← current rendered size
//   miscBuffer : vec4 = (life, seed, startSize, startSpeed)
//
// A particle is DEAD when age >= life. The update kernel parks dead particles
// far away with size 0 so the vertex stage produces a degenerate (invisible)
// quad — no GPU compaction or atomics needed.

/** Floats per particle across the three vec4 buffers (3 × 4). */
export const GPU_PARTICLE_VEC4S = 3;

/** Position the update kernel writes for dead particles (far below the world). */
export const GPU_PARTICLE_DEAD_Y = -1.0e9;

// ─── GPU emitter config ──────────────────────────────────────────────────────

/** Numeric shape-type codes passed to the spawn kernel as a uniform. */
export const enum GpuShapeCode {
  Point = 0,
  Sphere = 1,
  Hemisphere = 2,
  Box = 3,
  Cone = 4,
  Circle = 5,
}

/** Map an `EmitterShape` to its numeric kernel code. */
export function shapeCodeOf(shape: EmitterShape | undefined): GpuShapeCode {
  switch (shape?.type) {
    case 'sphere': return GpuShapeCode.Sphere;
    case 'hemisphere': return GpuShapeCode.Hemisphere;
    case 'box': return GpuShapeCode.Box;
    case 'cone': return GpuShapeCode.Cone;
    case 'circle': return GpuShapeCode.Circle;
    case 'point':
    default:
      return GpuShapeCode.Point;
  }
}

/** Force / turbulence options for the update kernel (all optional). */
export interface GpuForcesConfig {
  /** Gravity multiplier (× -9.81 on Y), same convention as the CPU emitter. */
  gravityModifier?: number;
  /** Per-second linear drag coefficient. velocity *= exp(-drag * dt). */
  drag?: number;
  /** Curl-noise turbulence. */
  curl?: {
    /** Peak acceleration magnitude added per second. */
    strength: number;
    /** Spatial frequency of the noise field (world-units⁻¹). */
    frequency: number;
    /** How fast the noise field scrolls through time. */
    scrollSpeed: number;
  };
  /** Optional point attractor. */
  attractor?: {
    point: THREE.Vector3;
    /** Acceleration toward the point at unit distance (falls off as 1/d²). */
    strength: number;
  };
}

/**
 * GPU-emitter config. A `ParticleConfig` (the CPU surface) plus GPU-only
 * force/turbulence extras. The pool capacity is rounded up to a power of two
 * at create time.
 */
export interface GpuParticleConfig extends ParticleConfig {
  /** Forces applied each frame by the update kernel. */
  forces?: GpuForcesConfig;
  /**
   * Optional per-particle size gate evaluated in the VERTEX stage: a callback
   * that takes the particle's world-space centre (a vec3 node) and returns a
   * float node in [0, 1] the billboard size is multiplied by. A gated
   * particle collapses to a degenerate quad — no simulation change, no branch.
   * Atlas gates rain and snow by its roof map (render wave 1, 2026-09-17).
   */
  sizeGate?: (worldCenter: unknown) => unknown;
}

// ─── 4-stop gradient / curve for over-life color + size ──────────────────────

/** A 4-stop color+alpha+size ramp baked into uniforms for the render material. */
export interface ParticleRamp {
  /** Sorted stop times in [0,1]. Length 4. */
  times: [number, number, number, number];
  /** RGBA per stop, each component in [0,1]. Length 4. */
  colors: [THREE.Vector4, THREE.Vector4, THREE.Vector4, THREE.Vector4];
  /** Size multiplier per stop. Length 4. */
  sizes: [number, number, number, number];
}
